import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 返回链接样式
const BackLink = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  color: '$textSecondary',
  fontSize: '0.9rem',
  textDecoration: 'none',
  marginBottom: '1rem',
  
  '&:hover': {
    color: '$accentPrimary'
  },
  
  '&::before': {
    content: '"←"',
    marginRight: '0.5rem',
    fontSize: '1.2rem'
  }
});

// 产品详情容器样式
const ProductDetailContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '2rem',
  
  '@media (min-width: 768px)': {
    gridTemplateColumns: '1fr 1fr'
  }
});

// 产品图片区域样式
const ProductImageSection = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 主图容器样式
const MainImageContainer = styled('div', {
  width: '100%',
  paddingTop: '75%', // 4:3 宽高比
  background: '$backgroundSecondary',
  borderRadius: '12px',
  position: 'relative',
  overflow: 'hidden'
});

// 主图样式
const MainImage = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'url("/assets/placeholder-miner.jpg") center/contain no-repeat',
  transition: 'transform 0.3s ease'
});

// 缩略图容器样式
const ThumbnailsContainer = styled('div', {
  display: 'flex',
  gap: '0.5rem',
  overflowX: 'auto',
  padding: '0.25rem'
});

// 缩略图样式
const Thumbnail = styled('div', {
  width: '70px',
  height: '70px',
  borderRadius: '6px',
  background: '$backgroundTertiary',
  cursor: 'pointer',
  flexShrink: 0,
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    border: '2px solid transparent',
    borderRadius: '6px',
    transition: 'border-color 0.2s ease'
  },
  
  variants: {
    active: {
      true: {
        '&::after': {
          borderColor: '$accentPrimary'
        }
      }
    }
  }
});

// 产品信息区域样式
const ProductInfoSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 产品标题样式
const ProductTitle = styled('h1', {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  marginBottom: '0.5rem'
});

// 产品制造商样式
const ProductManufacturer = styled('h2', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 产品价格样式
const ProductPrice = styled('div', {
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  margin: '1rem 0'
});

// 当前价格样式
const CurrentPrice = styled('span', {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '$accentPrimary'
});

// 原价样式
const OriginalPrice = styled('span', {
  fontSize: '1.2rem',
  textDecoration: 'line-through',
  color: '$textTertiary'
});

// 折扣标签样式
const DiscountBadge = styled('span', {
  padding: '0.25rem 0.5rem',
  background: '$accentPrimary',
  color: 'white',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  borderRadius: '4px'
});

// 产品描述样式
const ProductDescription = styled('p', {
  fontSize: '1rem',
  lineHeight: 1.6,
  color: '$textSecondary',
  marginBottom: '1rem'
});

// 按钮容器样式
const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '1rem',
  marginTop: '1rem'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      },
      secondary: {
        background: 'transparent',
        color: '$accentPrimary',
        border: '1px solid $accentPrimary',
        '&:hover': {
          background: 'rgba(6, 214, 160, 0.05)'
        }
      }
    },
    fullWidth: {
      true: {
        width: '100%'
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 规格标签样式
const SpecTag = styled('span', {
  padding: '0.25rem 0.75rem',
  background: '$backgroundTertiary',
  borderRadius: '20px',
  fontSize: '0.9rem',
  marginRight: '0.5rem',
  marginBottom: '0.5rem',
  display: 'inline-block',
  color: '$textSecondary'
});

// 规格标签容器样式
const SpecTagsContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: '1.5rem'
});

// 选项卡容器样式
const TabsContainer = styled('div', {
  borderBottom: '1px solid $borderColor',
  display: 'flex',
  gap: '1rem',
  marginTop: '3rem'
});

// 选项卡按钮样式
const TabButton = styled('button', {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: '$textSecondary',
  
  '&:hover': {
    color: '$textPrimary'
  },
  
  variants: {
    active: {
      true: {
        color: '$accentPrimary',
        borderBottomColor: '$accentPrimary'
      }
    }
  }
});

// 选项卡内容样式
const TabContent = styled('div', {
  padding: '2rem 0'
});

// 规格表格容器样式
const SpecTable = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%'
});

// 规格行样式
const SpecRow = styled('div', {
  display: 'flex',
  borderBottom: '1px solid $borderColor',
  
  '&:last-child': {
    borderBottom: 'none'
  },
  
  variants: {
    header: {
      true: {
        background: '$backgroundSecondary',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px'
      }
    }
  }
});

// 规格单元格样式
const SpecCell = styled('div', {
  padding: '1rem',
  flex: 1,
  fontSize: '0.9rem',
  
  variants: {
    label: {
      true: {
        color: '$textSecondary',
        fontWeight: 'bold',
        background: '$backgroundSecondary',
        borderRight: '1px solid $borderColor'
      }
    }
  }
});

// 相关产品容器样式
const RelatedProductsContainer = styled('div', {
  marginTop: '3rem'
});

// 相关产品标题样式
const RelatedProductsTitle = styled('h2', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  color: '$textPrimary'
});

// 相关产品网格样式
const RelatedProductsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '1rem'
});

// 相关产品卡片样式
const RelatedProductCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  background: '$backgroundSecondary',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// 相关产品图片容器样式
const RelatedProductImageContainer = styled('div', {
  width: '100%',
  paddingTop: '75%',
  position: 'relative'
});

// 相关产品图片样式
const RelatedProductImage = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'center/cover no-repeat'
});

// 相关产品信息样式
const RelatedProductInfo = styled('div', {
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 相关产品名称样式
const RelatedProductName = styled('h3', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 相关产品价格样式
const RelatedProductPrice = styled('span', {
  fontSize: '1rem',
  color: '$accentPrimary',
  fontWeight: 'bold'
});

/**
 * ASIC矿机详情页面组件
 * 显示单个矿机产品的详细信息
 */
const AsicShopDetails: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('specifications');
  const [selectedImage, setSelectedImage] = useState(0);
  
  // 从URL获取产品ID
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get('id');
  
  // 假设我们有一个函数从API获取产品数据
  // 实际项目中，这里应该发起API请求获取数据
  const getMinerById = (id: string | null) => {
    // 这里仅使用模拟数据
    return {
      id: 1,
      name: 'Antminer S21 Pro',
      manufacturer: 'Bitmain',
      description: '比特大陆最新旗舰矿机，采用最先进的3nm芯片工艺，提供卓越算力和能效比。S21 Pro是专业比特币挖矿的理想选择，带来行业领先的挖矿效率和稳定性。',
      price: 9999,
      originalPrice: 12999,
      discount: '23%',
      currency: 'USDT',
      images: [
        '/assets/miner-s21-main.jpg',
        '/assets/miner-s21-side.jpg',
        '/assets/miner-s21-front.jpg',
        '/assets/miner-s21-internals.jpg'
      ],
      specs: {
        hashrate: '230 TH/s',
        power: '3680W',
        efficiency: '16.0 J/TH',
        dimensions: '400mm × 195mm × 290mm',
        weight: '15.5kg',
        noise: '75db',
        temperature: '5°C - 40°C',
        algorithm: 'SHA-256',
        chipTechnology: '3nm',
        fans: '双风扇冷却系统',
        powerSupply: '内置电源',
        interface: '以太网 (RJ45)',
        warranty: '12个月'
      },
      features: [
        '超高算力：230 TH/s',
        '先进芯片：3nm制程工艺',
        '高能效比：16.0 J/TH',
        '智能温控系统',
        '全球技术支持'
      ],
      shipping: {
        inStock: true,
        leadTime: '3-5个工作日',
        shippingMethod: '国际快递/空运',
        shippingCost: '根据目的地区定'
      },
      warranty: {
        period: '12个月',
        coverage: '硬件故障',
        support: '7×24小时技术支持'
      }
    };
  };
  
  const miner = getMinerById(productId);
  
  // 相关产品（模拟数据）
  const relatedProducts = [
    {
      id: 2,
      name: 'Whatsminer M50S',
      image: '/assets/miner-m50s.jpg',
      price: 5499,
      currency: 'USDT'
    },
    {
      id: 5,
      name: 'Antminer E9 Pro',
      image: '/assets/miner-e9.jpg',
      price: 6899,
      currency: 'USDT'
    },
    {
      id: 3,
      name: 'Avalon A1366',
      image: '/assets/miner-a1366.jpg',
      price: 2999,
      currency: 'USDT'
    }
  ];
  
  if (!miner) {
    return (
      <PageContainer>
        <div>{t('asicShop.productNotFound', '找不到该产品')}</div>
        <BackLink to="/products/asic-shop/products">
          {t('common.backToProducts', '返回产品列表')}
        </BackLink>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <BackLink to="/products/asic-shop/products">
        {t('common.backToProducts', '返回产品列表')}
      </BackLink>
      
      <ProductDetailContainer>
        {/* 产品图片区域 */}
        <ProductImageSection>
          <MainImageContainer>
            <MainImage style={{ background: `url(${miner.images[selectedImage] || '/assets/placeholder-miner.jpg'}) center/contain no-repeat` }} />
          </MainImageContainer>
          
          <ThumbnailsContainer>
            {miner.images.map((image, index) => (
              <Thumbnail 
                key={index}
                active={selectedImage === index}
                onClick={() => setSelectedImage(index)}
                style={{ 
                  background: `url(${image}) center/cover no-repeat`
                }}
              />
            ))}
          </ThumbnailsContainer>
        </ProductImageSection>
        
        {/* 产品信息区域 */}
        <ProductInfoSection>
          <div>
            <ProductTitle>{miner.name}</ProductTitle>
            <ProductManufacturer>{t('asicShop.by', '制造商')}: {miner.manufacturer}</ProductManufacturer>
            
            <SpecTagsContainer>
              {miner.features.map((feature, index) => (
                <SpecTag key={index}>{feature}</SpecTag>
              ))}
            </SpecTagsContainer>
            
            <ProductDescription>{miner.description}</ProductDescription>
            
            <ProductPrice>
              <CurrentPrice>{miner.price} {miner.currency}</CurrentPrice>
              {miner.originalPrice && (
                <>
                  <OriginalPrice>{miner.originalPrice} {miner.currency}</OriginalPrice>
                  <DiscountBadge>{miner.discount} {t('asicShop.off', '优惠')}</DiscountBadge>
                </>
              )}
            </ProductPrice>
            
            <div>
              <span style={{ color: '$textSecondary', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                {miner.shipping.inStock 
                  ? t('asicShop.inStock', '有库存') 
                  : t('asicShop.outOfStock', '缺货')}
              </span>
              <span style={{ color: '$textTertiary', fontSize: '0.9rem' }}>
                {t('asicShop.shipping.leadTime', '发货时间')}: {miner.shipping.leadTime}
              </span>
            </div>
            
            <ButtonContainer>
              <Button variant="primary">
                {t('asicShop.buyNow', '立即购买')}
              </Button>
              <Button variant="secondary">
                {t('asicShop.customize', '定制配置')}
              </Button>
            </ButtonContainer>
          </div>
        </ProductInfoSection>
      </ProductDetailContainer>
      
      {/* 选项卡内容 */}
      <div>
        <TabsContainer>
          <TabButton 
            active={activeTab === 'specifications'}
            onClick={() => setActiveTab('specifications')}
          >
            {t('asicShop.tabs.specifications', '技术参数')}
          </TabButton>
          <TabButton 
            active={activeTab === 'shipping'}
            onClick={() => setActiveTab('shipping')}
          >
            {t('asicShop.tabs.shipping', '配送信息')}
          </TabButton>
          <TabButton 
            active={activeTab === 'warranty'}
            onClick={() => setActiveTab('warranty')}
          >
            {t('asicShop.tabs.warranty', '保修政策')}
          </TabButton>
        </TabsContainer>
        
        <TabContent>
          {activeTab === 'specifications' && (
            <SpecTable>
              {Object.entries(miner.specs).map(([key, value], index) => (
                <SpecRow key={index}>
                  <SpecCell label>{t(`asicShop.specs.${key}`, key)}</SpecCell>
                  <SpecCell>{value}</SpecCell>
                </SpecRow>
              ))}
            </SpecTable>
          )}
          
          {activeTab === 'shipping' && (
            <div>
              <SpecTable>
                <SpecRow>
                  <SpecCell label>{t('asicShop.shipping.leadTime', '发货时间')}</SpecCell>
                  <SpecCell>{miner.shipping.leadTime}</SpecCell>
                </SpecRow>
                <SpecRow>
                  <SpecCell label>{t('asicShop.shipping.method', '配送方式')}</SpecCell>
                  <SpecCell>{miner.shipping.shippingMethod}</SpecCell>
                </SpecRow>
                <SpecRow>
                  <SpecCell label>{t('asicShop.shipping.cost', '运费')}</SpecCell>
                  <SpecCell>{miner.shipping.shippingCost}</SpecCell>
                </SpecRow>
              </SpecTable>
              
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                  {t('asicShop.shipping.notes', '配送说明')}
                </h3>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '$textSecondary' }}>
                  {t('asicShop.shipping.notesContent', '我们使用专业的物流服务，确保您的矿机安全送达。所有设备在发货前都会经过全面测试，并使用防震包装。国际运输时间可能因目的地国家/地区的海关政策而有所不同。')}
                </p>
              </div>
            </div>
          )}
          
          {activeTab === 'warranty' && (
            <div>
              <SpecTable>
                <SpecRow>
                  <SpecCell label>{t('asicShop.warranty.period', '保修期')}</SpecCell>
                  <SpecCell>{miner.warranty.period}</SpecCell>
                </SpecRow>
                <SpecRow>
                  <SpecCell label>{t('asicShop.warranty.coverage', '保修范围')}</SpecCell>
                  <SpecCell>{miner.warranty.coverage}</SpecCell>
                </SpecRow>
                <SpecRow>
                  <SpecCell label>{t('asicShop.warranty.support', '技术支持')}</SpecCell>
                  <SpecCell>{miner.warranty.support}</SpecCell>
                </SpecRow>
              </SpecTable>
              
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                  {t('asicShop.warranty.policy', '保修政策')}
                </h3>
                <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '$textSecondary' }}>
                  {t('asicShop.warranty.policyContent', '我们的保修政策涵盖所有制造缺陷和硬件故障。保修期内，我们提供免费维修或更换服务。保修不包括因不当使用、改装或自然灾害造成的损坏。请注意，拆开设备外壳可能会导致保修失效。')}
                </p>
              </div>
            </div>
          )}
        </TabContent>
      </div>
      
      {/* 相关产品推荐 */}
      <RelatedProductsContainer>
        <RelatedProductsTitle>{t('asicShop.relatedProducts', '相关产品推荐')}</RelatedProductsTitle>
        <RelatedProductsGrid>
          {relatedProducts.map(product => (
            <Link key={product.id} to={`/products/asic-shop/details?id=${product.id}`} style={{ textDecoration: 'none' }}>
              <RelatedProductCard>
                <RelatedProductImageContainer>
                  <RelatedProductImage style={{ background: `url(${product.image || '/assets/placeholder-miner.jpg'}) center/cover no-repeat` }} />
                </RelatedProductImageContainer>
                <RelatedProductInfo>
                  <RelatedProductName>{product.name}</RelatedProductName>
                  <RelatedProductPrice>{product.price} {product.currency}</RelatedProductPrice>
                </RelatedProductInfo>
              </RelatedProductCard>
            </Link>
          ))}
        </RelatedProductsGrid>
      </RelatedProductsContainer>
    </PageContainer>
  );
};

export default AsicShopDetails; 